import i18n, { DEFAULT_LOCALE } from '@/utils/i18n';
import {
  useDynamicConfigValue,
  useSupplierDynamicConfigValue,
} from './use-dynamic-config-value';
import { useMemo } from 'react';
import { useUserContext } from '@/contexts/user-context';

export const useItemQueryLocale = () => {
  const { translationsEnabled } = useUserContext();
  return translationsEnabled ? i18n.locale : DEFAULT_LOCALE;
};

// Until broader rollout, always pass false for useSaltCache unless manually
// overriding for specific queries.
export const useBaseItemQueryVariables = () => {
  const locale = useItemQueryLocale();
  const saltApiIntegrationEnabled = useDynamicConfigValue(
    'salt_api_integration_enabled',
  );

  return useMemo(
    () => ({
      locale,
      skipLiveSalt: !saltApiIntegrationEnabled,
      skipSaltApi: !saltApiIntegrationEnabled,
      useSaltCache: false,
    }),
    [locale, saltApiIntegrationEnabled],
  );
};

export type BaseItemQueryVariables = ReturnType<
  typeof useBaseItemQueryVariables
>;

// Ignore chat overrides for screens displaying items from multiple chats
export const useBaseSupplierItemQueryVariables = () => {
  const locale = useItemQueryLocale();
  const saltApiIntegrationEnabled = useSupplierDynamicConfigValue(
    'salt_api_integration_enabled',
  );
  return useMemo<BaseItemQueryVariables>(
    () => ({
      locale,
      skipLiveSalt: !saltApiIntegrationEnabled,
      skipSaltApi: !saltApiIntegrationEnabled,
      useSaltCache: false,
    }),
    [locale, saltApiIntegrationEnabled],
  );
};

export const useItemQueryVariables = () => {
  const baseVariables = useBaseItemQueryVariables();
  const itemGroupsEnabled = useDynamicConfigValue('item_groups_enabled');

  const skipGroupMemberships = !itemGroupsEnabled;

  return useMemo(
    () => ({ ...baseVariables, skipGroupMemberships }),
    [baseVariables, skipGroupMemberships],
  );
};

// Order guide only. The order guide reads from variant_pack_salt_cache if both
// salt_api_integration_enabled and salt_cache_read_enabled flags are on.
export const useOrderGuideItemQueryVariables = () => {
  const baseVariables = useItemQueryVariables();
  const saltApiIntegrationEnabled = useDynamicConfigValue(
    'salt_api_integration_enabled',
  );
  const saltCacheReadEnabled = useDynamicConfigValue('salt_cache_read_enabled');

  const useSaltCache = Boolean(
    saltApiIntegrationEnabled && saltCacheReadEnabled,
  );

  return useMemo(
    () => ({
      ...baseVariables,
      skipLiveSalt: baseVariables.skipSaltApi || useSaltCache,
      useSaltCache,
    }),
    [baseVariables, useSaltCache],
  );
};
